// @ts-nocheck

import { find, reduce } from 'lodash';
import { createSelector } from 'reselect';

import { Target } from '../client';
import { isBetshareEnabled } from '../constants/features';
import { type BasketItemState } from '../states/basket';
import { type MatchState } from '../states/entities/matches';
import { type StateType } from '../states/index';
import { BasketStatuses, BasketModes, BasketItemStatuses } from '../types/basket';
import { type Selector } from '../types/selectors';
import { type SelectorMaker } from '../types/selectors';
import {
  hasAtLeastOneValidatedOrWaitingOrRejected,
  isStatusIsValidatedOrWaitingOrRejected,
} from '../utils/basketItems';

import { getItemIdFromProps } from './_propsGetters_';
import {
  selectBasketBetslipId,
  selectBasketIsKeyboardVisible,
  selectBasketItems,
  selectBasketItemsEntity,
  selectBasketMode,
  selectBasketSelectedFreebetId,
  selectBasketStake,
  selectBasketStatus,
  selectBasketSystems,
} from './basket/basketPrimary.selector';
import { selectBasketEnhancedItems, selectBasketNbItemInError, selectNbBasketItems } from './basketItems.selector';
import { selectEntitiesMatches } from './entities/matches/entitiesMatchesPrimary.selector';

// no-memoize
export const selectBasketNbBetsSystem: Selector<number> = (state: StateType) => {
  const systems = selectBasketSystems(state);

  return reduce(systems, (acc, { count, checked }) => (checked ? acc + count : acc), 0);
};

// no-memoize
export const selectBasketTotalCostSystem: Selector<number> = (state: StateType) => {
  const systems = selectBasketSystems(state);
  const stake = selectBasketStake(state);

  return reduce(
    systems,
    (acc, { count, checked }) => (checked ? acc + Math.round(count * (parseFloat(stake) || 0) * 100) / 100 : acc),
    0,
  );
};

// no-memoize
const selectBasketTotalCostCombined: Selector<number> = (state: StateType) => {
  const stake = selectBasketStake(state);

  if (typeof stake === 'string') {
    return parseFloat(stake) ?? 0;
  }

  return stake;
};

export const selectBasketTotalCostSimple: Selector<number> = createSelector([selectBasketEnhancedItems], (items) =>
  reduce(items, (acc, { stake }) => Math.round((acc + (parseFloat(stake) || 0)) * 100) / 100, 0),
);

export const selectBasketTotalCost: Selector<number> = createSelector(
  [selectBasketMode, selectBasketTotalCostSimple, selectBasketTotalCostCombined, selectBasketTotalCostSystem],
  (mode, simpleTotalCost, combinedTotalCost, systemTotalCost) => {
    if (mode === BasketModes.SIMPLE) {
      return simpleTotalCost;
    }
    if (mode === BasketModes.COMBINED) {
      return combinedTotalCost;
    }
    if (mode === BasketModes.SYSTEM) {
      return systemTotalCost;
    }
    return 0;
  },
);

export const selectBasketShowResume: Selector<boolean> = createSelector(
  [selectBasketEnhancedItems, selectBasketStatus],
  (items, basketStatus) => {
    if (basketStatus !== BasketStatuses.PENDING) {
      if (hasAtLeastOneValidatedOrWaitingOrRejected(items)) {
        return true;
      }

      if (isStatusIsValidatedOrWaitingOrRejected(basketStatus)) {
        return true;
      }
    }

    return false;
  },
);

// no-memoize
export const selectBasketIsSummaryKeyboardVisible: Selector<boolean> = (state: StateType) => {
  const isKeyboardVisible = selectBasketIsKeyboardVisible(state);
  const nbItems = selectNbBasketItems(state);
  const status = selectBasketStatus(state);
  const mode = selectBasketMode(state);

  const isOneSimpleBet = mode === BasketModes.SIMPLE && nbItems === 1;

  return Target.__MOBILE__ ? status !== BasketStatuses.PENDING && (isKeyboardVisible || isOneSimpleBet) : false;
};

// no-memoize
export const selectBasketIsInputDisabled: Selector<boolean> = (state: StateType) =>
  selectBasketStatus(state) === BasketStatuses.PENDING;

export const makeBasketItemIdSelector: SelectorMaker<number | null> = () =>
  createSelector([selectBasketMode, selectBasketEnhancedItems, getItemIdFromProps], (mode, items, itemIdModeSimple) => {
    const isModeSimple = mode === BasketModes.SIMPLE;
    const isMultipleSimpleBets = isModeSimple && items.length > 1;
    const isOneSimpleBet = isModeSimple && items.length === 1;

    let itemId = null;
    if (isOneSimpleBet && !itemIdModeSimple) {
      itemId = items[0].oddId;
    } else if (isMultipleSimpleBets && itemIdModeSimple) {
      itemId = itemIdModeSimple;
    }
    return itemId;
  });

export const makeBasketItemSelector: SelectorMaker<BasketItemState | undefined> = () =>
  createSelector([selectBasketEnhancedItems, makeBasketItemIdSelector()], (items, itemId) =>
    find(items, (item) => item.oddId === itemId),
  );

export const makeBasketItemIsValidatedSelector: SelectorMaker<boolean> = () =>
  createSelector([makeBasketItemSelector()], (item) => item?.status === BasketItemStatuses.VALIDATED);

export const makeBasketActiveStakeSelector: SelectorMaker<string | number | undefined> = () =>
  createSelector([selectBasketStake, makeBasketItemSelector()], (basketStake, item) => {
    let stake = basketStake;
    if (item) {
      stake = item.stake;
    }
    return stake;
  });

export const makeBasketIsFreebetSelector: SelectorMaker<boolean> = () =>
  createSelector([selectBasketSelectedFreebetId, makeBasketItemSelector()], (basketSelectedFreebetId, item) => {
    let isFreebet = typeof basketSelectedFreebetId === 'boolean' ? basketSelectedFreebetId : false;
    if (item) {
      isFreebet = typeof item.selectedFreebetId === 'boolean' ? item.selectedFreebetId : false;
    }
    return isFreebet;
  });

// no-memoize
export const selectBasketShouldShowMainBetConfirmation: Selector<boolean> = (state: StateType) => {
  const nbItems = selectNbBasketItems(state);
  const status = selectBasketStatus(state);
  const mode = selectBasketMode(state);

  return (
    status === BasketStatuses.VALIDATED &&
    ((mode === BasketModes.SIMPLE && nbItems === 1) || mode === BasketModes.COMBINED || mode === BasketModes.SYSTEM)
  );
};

export const selectBetshareBasketBetslipReference: Selector<string | null> = createSelector(
  [
    selectBasketMode,
    selectBasketBetslipId,
    selectBasketStatus,
    selectBasketShowResume,
    selectBasketNbItemInError,
    selectBasketEnhancedItems,
  ],
  (mode, betslipId, status, showResume, nbItemInError, items) => {
    let betshareBetslipId = null;

    if (isBetshareEnabled && showResume) {
      const isValidatedAndNotSimple = mode !== BasketModes.SIMPLE && status === BasketStatuses.VALIDATED;
      if (isValidatedAndNotSimple) {
        betshareBetslipId = betslipId;
      }
      const isSimpleAndHasOneValidated =
        mode === BasketModes.SIMPLE &&
        nbItemInError === 0 &&
        items.length === 1 &&
        items[0] &&
        items[0].status === BasketItemStatuses.VALIDATED;
      if (isSimpleAndHasOneValidated) {
        betshareBetslipId = items[0].betslipId;
      }
    }
    return betshareBetslipId;
  },
);

export const makeBasketItemsMatchesSelector: SelectorMaker<Array<MatchState>> = () =>
  createSelector(
    [selectEntitiesMatches, selectBasketItemsEntity, selectBasketItems],
    (matchesState, basketItem, basketItems) => {
      if (Object.keys(basketItem ?? {}).length) {
        return [matchesState[basketItem.matchId]];
      }

      return Object.keys(basketItems).map((key) => {
        return matchesState[basketItems[key].matchId];
      });
    },
  );
